import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import SignatureCanvas from 'react-signature-canvas';
import { V2QsSignatureProps } from './types';

/**
 * V2QsSignature 컴포넌트
 *
 * 서명 입력을 위한 캔버스 컴포넌트
 * V1 QsInputSignature의 V2 버전으로 순수 UI만 담당
 *
 * @example
 * ```tsx
 * <V2QsSignature
 *   value={signature}
 *   onChange={setSignature}
 *   signatureHint="전자 서명"
 *   resetButtonText="다시 하기"
 * />
 * ```
 */
function V2QsSignature({
  value,
  onChange,
  width: propWidth = 475,
  height = 150,
  penColor = 'black',
  error = false,
  errorMessage,
  disabled = false,
  readOnly = false,
  fullWidth = true,
  className,
  containerClassName,
  label,
  hint,
  signatureHint = 'Electronic Signature',
  resetButtonText = 'Reset',
  showResetButton = true,
}: V2QsSignatureProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sigCanvasRef = useRef<any>(null);

  // 뷰포트에 맞게 너비 조정
  const width = useMemo(() => {
    if (typeof window === 'undefined') return propWidth;
    return Math.floor(Math.min(propWidth, 0.8 * window.innerWidth));
  }, [propWidth]);

  // 초기값 로드
  useEffect(() => {
    if (value && sigCanvasRef.current) {
      sigCanvasRef.current.fromDataURL(value, { width, height });
    }
  }, [value, width, height]);

  // 서명 완료 핸들러
  const handleEnd = useCallback(() => {
    if (disabled || readOnly) return;

    const dataUrl = sigCanvasRef.current?.toDataURL('image/png');
    if (dataUrl) {
      onChange?.(dataUrl);
    }
  }, [onChange, disabled, readOnly]);

  // 리셋 핸들러
  const handleReset = useCallback(() => {
    if (disabled || readOnly) return;

    sigCanvasRef.current?.clear();
    onChange?.('');
  }, [onChange, disabled, readOnly]);

  const hasError = error;
  const hasValue = !!value;

  // Container 클래스
  const containerClasses = classNames('flex flex-col gap-1', containerClassName);

  // Canvas wrapper 클래스
  const canvasWrapperClasses = classNames(
    'transition-all duration-200 rounded-[10px] p-2',
    'border-[1.5px]',
    {
      // Width
      'w-full': fullWidth,

      // Border color
      'border-[#E6E9F5]': !hasError && !disabled,
      'border-[#FF5E71]': hasError,

      // Background
      'bg-white': !disabled,
      'bg-[#F2F4FC]': disabled,
    },
    className
  );

  // Reset 버튼 클래스
  const resetButtonClasses = classNames(
    'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200',
    {
      'bg-[#F2F4FC] text-[#222638] hover:bg-[#E6E9F5]': !disabled,
      'bg-[#F2F4FC] text-[#AFB2C2] cursor-not-allowed': disabled,
    }
  );

  // 라벨 클래스
  const labelClasses = 'text-sm font-medium text-[#222638] mb-1';

  // 힌트 클래스
  const hintClasses = 'text-xs text-[#AFB2C2]';

  // 에러 메시지 클래스
  const errorClasses = 'text-xs text-[#FF5E71]';

  // 서명 힌트 클래스
  const signatureHintClasses = 'text-sm text-[#AFB2C2] p-2';

  return (
    <div className={containerClasses}>
      {/* Label */}
      {label && <label className={labelClasses}>{label}</label>}

      {/* Signature Canvas */}
      <div className="flex flex-col gap-2">
        <div className={canvasWrapperClasses}>
          {/* 읽기 전용 모드 */}
          {readOnly && value && (
            <img
              src={value}
              alt="Signature"
              style={{ width, height }}
              className="object-contain"
            />
          )}
          {readOnly && !value && (
            <div
              style={{ width, height }}
              className="flex items-center justify-center text-[#AFB2C2]"
            >
              No signature
            </div>
          )}
          {/* 편집 모드: 캔버스 표시 */}
          {!readOnly && (
            <SignatureCanvas
              ref={sigCanvasRef}
              penColor={penColor}
              canvasProps={{
                width,
                height,
                className: classNames('sigCanvas', {
                  'cursor-not-allowed opacity-50': disabled,
                }),
              }}
              onEnd={handleEnd}
            />
          )}
        </div>

        {/* Footer: Hint & Reset Button */}
        <div className="flex items-center justify-between">
          {/* Signature Hint */}
          <span className={signatureHintClasses}>{signatureHint}</span>

          {/* Reset Button */}
          {showResetButton && !readOnly && (
            <button
              type="button"
              className={resetButtonClasses}
              onClick={handleReset}
              disabled={disabled || !hasValue}
            >
              {resetButtonText}
            </button>
          )}
        </div>
      </div>

      {/* Hint */}
      {hint && !hasError && <span className={hintClasses}>{hint}</span>}

      {/* Error Message */}
      {hasError && errorMessage && (
        <span className={errorClasses}>{errorMessage}</span>
      )}
    </div>
  );
}

export default memo(V2QsSignature);
